import React, { useState } from 'react';
import { Modal, Input, DatePicker, Upload, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { uploadFiles } from '~/services/hooks/files';
import { updateInvoice } from '~/services/hooks/invoices';

export default function NotaryLienModal({ visible, onCancel, onSave, selectedRecord }) {
  // initial states already preloaded from selectedRecord
  const [notaryName, setNotaryName] = useState(selectedRecord.lienWaiver.notary || '');
  const [commissionExpiresDate, setCommissionExpiresDate] = useState(
    selectedRecord.lienWaiver.commissionExpiresDate ? dayjs(selectedRecord.lienWaiver.commissionExpiresDate) : null
  );
  const [uploadList, setUploadList] = useState([]);
  const [loading, setLoading] = useState(false);

  // validation before upload (image and <5MB)
  const beforeUpload = file => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Only images are allowed!');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // updates the list of selected files
  const handleUpload = ({ fileList }) => {
    setUploadList(fileList);
  };

  // On confirm, uploads, merges attachments and persists everything
  const handleConfirm = async () => {
    setLoading(true);
    try {
      const attachmentsArray = [];

      if (uploadList.length) {
        // 1) send images to backend
        const uploaded = await uploadFiles(uploadList);
        // 2) format the response for the attachments field
        uploaded.forEach(file => {
          attachmentsArray.push({ id: null, arquive: file });
        });
      }

      // 3) build the complete invoice payload
      const payload = {
        ...selectedRecord,
        // attachments: [...(selectedRecord.attachments || []), ...attachmentsArray],
        lienWaiver: {
          ...selectedRecord.lienWaiver,
          notary: notaryName,
          commissionExpiresDate: commissionExpiresDate
            ? dayjs(commissionExpiresDate).format('YYYY-MM-DD HH:mm:ss')
            : '',
          // uses the URL of the first image, or keeps the existing one
          // notaryImage:
          //   attachmentsArray[0]?.arquive?.url ||
          //   selectedRecord.lienWaiver.notaryImage ||
          //   '',
          notaryImage: attachmentsArray.length ? attachmentsArray[0].arquive : selectedRecord.lienWaiver.notaryImage,
        },
      };

      // 4) update on the server
      // delete payload.lienWaiver.notaryImage; // remove the lienWaiver.notaryImage from payload
      const data = await updateInvoice(payload);
      message.success('Invoice updated successfully');
      onSave(data);
    } catch (err) {
      console.error(err);
      message.error('Error updating invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Notary Acknowledgment"
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Save"
      confirmLoading={loading}
      width={600}
    >
      <p>Notary Name:</p>
      <Input value={notaryName} onChange={e => setNotaryName(e.target.value)} />

      <p style={{ marginTop: 12 }}>Commission Expiration Date:</p>
      <DatePicker
        style={{ width: '100%' }}
        value={commissionExpiresDate}
        onChange={setCommissionExpiresDate}
        format="YYYY-MM-DD"
      />

      <p style={{ marginTop: 12 }}>Stamp Upload (image):</p>
      <Upload
        name="file"
        accept="image/*"
        beforeUpload={beforeUpload}
        onChange={handleUpload}
        fileList={uploadList}
        maxCount={1}
        listType="picture"
      >
        <Button icon={<UploadOutlined />}>Select Image</Button>
      </Upload>
    </Modal>
  );
}
